// =============================================
// Industry Pricing Templates - Field Service Focus
// Used for guided pricing setup during onboarding
// =============================================

export const INDUSTRY_PRICING_TEMPLATES = {
  // ===== EXTERIOR CLEANING =====
  PressureWashing: {
    defaultMethod: 'per-sqft',
    measurementLabel: 'Square Feet',
    measurementUnit: 'sq ft',
    friendlyLabels: [
      { label: 'Small Home (1-story ranch)', estimate: 1200, description: 'Under 1,500 sq ft' },
      { label: 'Medium Home (2-story)', estimate: 2000, description: '1,500 - 2,500 sq ft' },
      { label: 'Large Home (2-story+)', estimate: 3000, description: '2,500+ sq ft' },
    ],
    setupPrompt: 'What do you charge for a typical 2-story house wash?',
    typicalEstimate: 2000,
    services: ['House Wash', 'Driveway', 'Deck/Patio', 'Roof Wash', 'Gutter Cleaning'],
  },
  
  WindowWashing: {
    defaultMethod: 'per-unit',
    measurementLabel: 'Number of Windows',
    measurementUnit: 'windows',
    friendlyLabels: [
      { label: 'Small Home', estimate: 12, description: '10-15 windows' },
      { label: 'Medium Home', estimate: 20, description: '16-25 windows' },
      { label: 'Large Home', estimate: 30, description: '26+ windows' },
    ],
    setupPrompt: 'What do you charge per window?',
    typicalEstimate: null,
    services: ['Interior Windows', 'Exterior Windows', 'Screens', 'Tracks'],
  },
  
  GutterCleaning: {
    defaultMethod: 'per-linear-ft',
    measurementLabel: 'Linear Feet',
    measurementUnit: 'ln ft',
    friendlyLabels: [
      { label: 'Small Home', estimate: 100, description: '~100 linear ft' },
      { label: 'Average Home', estimate: 150, description: '~150 linear ft' },
      { label: 'Large Home', estimate: 250, description: '250+ linear ft' },
    ],
    setupPrompt: 'What do you charge per linear foot of gutter?',
    typicalEstimate: 150,
    services: ['Gutter Cleaning', 'Gutter Flush', 'Downspout Clearing', 'Gutter Brightening'],
  },
  
  // ===== INTERIOR SERVICES =====
  HouseCleaning: {
    defaultMethod: 'tiered',
    measurementLabel: 'Home Size',
    measurementUnit: 'bedrooms',
    friendlyLabels: [
      { label: '1-2 Bedroom', estimate: null, description: 'Apartment or small home' },
      { label: '3 Bedroom', estimate: null, description: 'Average home' },
      { label: '4+ Bedroom', estimate: null, description: 'Large home' },
    ],
    setupPrompt: 'What do you charge for a 3-bedroom home?',
    typicalEstimate: null,
    services: ['Standard Clean', 'Deep Clean', 'Move-In/Move-Out'],
  },
  
  CarpetCleaning: {
    defaultMethod: 'per-sqft',
    measurementLabel: 'Square Feet',
    measurementUnit: 'sq ft',
    friendlyLabels: [
      { label: 'Single Room', estimate: 200, description: 'Up to 200 sq ft' },
      { label: 'Living Areas', estimate: 500, description: '~500 sq ft' },
      { label: 'Whole House', estimate: 1500, description: '1,500+ sq ft' },
    ],
    setupPrompt: 'What do you charge per square foot?',
    typicalEstimate: 500,
    services: ['Room Cleaning', 'Whole House', 'Stain Treatment', 'Pet Odor Treatment', 'Upholstery'],
  },
  
  // ===== LAWN & LANDSCAPE =====
  Landscaping: {
    defaultMethod: 'per-sqft',
    measurementLabel: 'Square Feet',
    measurementUnit: 'sq ft',
    friendlyLabels: [
      { label: 'Small Yard', estimate: 5000, description: '⅛ acre or less' },
      { label: 'Average Yard', estimate: 10000, description: '¼ acre' },
      { label: 'Large Yard', estimate: 20000, description: '½ acre' },
      { label: 'XL Yard', estimate: 43560, description: '1+ acre' },
    ],
    setupPrompt: 'What do you charge for a typical ¼ acre lawn?',
    typicalEstimate: 10000,
    services: ['Lawn Mowing', 'Edging', 'Leaf Removal', 'Fertilizing', 'Aeration'],
  },
  
  TreeService: {
    defaultMethod: 'per-unit',
    measurementLabel: 'Number of Trees',
    measurementUnit: 'trees',
    friendlyLabels: [
      { label: 'Small Tree', estimate: 1, description: 'Under 20 ft' },
      { label: 'Medium Tree', estimate: 1, description: '20-40 ft' },
      { label: 'Large Tree', estimate: 1, description: '40+ ft' },
    ],
    setupPrompt: 'What do you charge to trim a medium-sized tree?',
    typicalEstimate: 1,
    services: ['Tree Trimming', 'Tree Removal', 'Stump Grinding', 'Emergency Service'],
  },
  
  // ===== SEASONAL =====
  ChristmasLights: {
    defaultMethod: 'per-linear-ft',
    measurementLabel: 'Linear Feet',
    measurementUnit: 'ln ft',
    friendlyLabels: [
      { label: '1-Story Ranch', estimate: 120, description: '~120 linear ft' },
      { label: '2-Story Colonial', estimate: 180, description: '~180 linear ft' },
      { label: 'Large/Complex Home', estimate: 250, description: '250+ linear ft' },
    ],
    setupPrompt: 'What do you charge for a typical 2-story roofline?',
    typicalEstimate: 180,
    services: ['Roofline Install', 'Bush/Tree Wrap', 'Takedown', 'Storage'],
  },
  
  // ===== SPECIALTY =====
  GlassMirror: {
    defaultMethod: 'per-sqft',
    measurementLabel: 'Square Feet',
    measurementUnit: 'sq ft',
    friendlyLabels: [
      { label: 'Small (medicine cabinet)', estimate: 2, description: '1-3 sq ft' },
      { label: 'Medium (bathroom mirror)', estimate: 8, description: '4-12 sq ft' },
      { label: 'Large (wall mirror)', estimate: 20, description: '12+ sq ft' },
    ],
    setupPrompt: 'What do you charge per square foot of glass?',
    typicalEstimate: null,
    services: ['Mirror Install', 'Glass Repair', 'Shower Door', 'Window Replacement'],
  },
  
  MobileDetailing: {
    defaultMethod: 'tiered',
    measurementLabel: 'Vehicle Size',
    measurementUnit: 'vehicle',
    friendlyLabels: [
      { label: 'Sedan/Coupe', estimate: null, description: 'Cars, small SUVs' },
      { label: 'SUV/Truck', estimate: null, description: 'Full-size SUVs, trucks' },
      { label: 'XL/Commercial', estimate: null, description: 'Vans, large trucks' },
    ],
    setupPrompt: 'What do you charge for a full detail on a sedan?',
    typicalEstimate: null,
    services: ['Exterior Wash', 'Interior Detail', 'Full Detail', 'Paint Correction', 'Ceramic Coating'],
  },
  
  // ===== TRADES =====
  HVAC: {
    defaultMethod: 'flat-rate',
    measurementLabel: null,
    measurementUnit: null,
    friendlyLabels: null,
    setupPrompt: 'What do you charge for an AC tune-up?',
    typicalEstimate: null,
    services: ['AC Tune-Up', 'Heating Tune-Up', 'Diagnostic', 'Filter Replacement', 'Duct Cleaning'],
  },
  
  Plumbing: {
    defaultMethod: 'per-hour',
    measurementLabel: 'Hours',
    measurementUnit: 'hours',
    friendlyLabels: null,
    setupPrompt: 'What is your hourly rate?',
    typicalEstimate: 1,
    services: ['Drain Cleaning', 'Faucet Repair', 'Toilet Repair', 'Water Heater', 'Leak Detection'],
  },
  
  Electrical: {
    defaultMethod: 'per-hour',
    measurementLabel: 'Hours',
    measurementUnit: 'hours',
    friendlyLabels: null,
    setupPrompt: 'What is your hourly rate?',
    typicalEstimate: 1,
    services: ['Outlet Install', 'Light Fixture', 'Ceiling Fan', 'Panel Inspection', 'Troubleshooting'],
  },
  
  Handyman: {
    defaultMethod: 'per-hour',
    measurementLabel: 'Hours',
    measurementUnit: 'hours',
    friendlyLabels: null,
    setupPrompt: 'What is your hourly rate?',
    typicalEstimate: 1,
    services: ['General Repairs', 'Assembly', 'Mounting/Hanging', 'Minor Plumbing', 'Minor Electrical'],
  },
  
  // ===== POOL & PEST =====
  PoolService: {
    defaultMethod: 'flat-rate',
    measurementLabel: null,
    measurementUnit: null,
    friendlyLabels: null,
    setupPrompt: 'What do you charge for weekly pool service?',
    typicalEstimate: null,
    services: ['Weekly Service', 'One-Time Cleaning', 'Pool Opening', 'Pool Closing', 'Green Pool Recovery'],
  },
  
  PestControl: {
    defaultMethod: 'flat-rate',
    measurementLabel: null,
    measurementUnit: null,
    friendlyLabels: null,
    setupPrompt: 'What do you charge for a general pest treatment?',
    typicalEstimate: null,
    services: ['General Treatment', 'Quarterly Service', 'Termite Inspection', 'Rodent Control', 'Bed Bugs'],
  },
  
  // ===== OTHER =====
  JunkRemoval: {
    defaultMethod: 'tiered',
    measurementLabel: 'Load Size',
    measurementUnit: 'load',
    friendlyLabels: [
      { label: '¼ Truck', estimate: null, description: 'A few items' },
      { label: '½ Truck', estimate: null, description: 'Room cleanout' },
      { label: 'Full Truck', estimate: null, description: 'Garage/estate cleanout' },
    ],
    setupPrompt: 'What do you charge for a half truck load?',
    typicalEstimate: null,
    services: ['Furniture Removal', 'Appliance Removal', 'Yard Debris', 'Estate Cleanout'],
  },
  
  Other: {
    defaultMethod: 'flat-rate',
    measurementLabel: null,
    measurementUnit: null,
    friendlyLabels: null,
    setupPrompt: null,
    typicalEstimate: null,
    services: [],
  },
};

// Helper: Get template for an industry
export const getIndustryTemplate = (industryType) => {
  return INDUSTRY_PRICING_TEMPLATES[industryType] || INDUSTRY_PRICING_TEMPLATES.Other;
};

// Helper: Get friendly label from measurement
export const getFriendlyLabel = (measurementValue, friendlyLabels) => {
  if (!friendlyLabels || !measurementValue) return null;
  
  // Sort by estimate ascending
  const sorted = [...friendlyLabels]
    .filter(l => l.estimate != null)
    .sort((a, b) => a.estimate - b.estimate);
  
  if (sorted.length === 0) return null;
  
  // Find the best match
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    
    // If no next, or value is closer to current than next
    if (!next) {
      return current.label;
    }
    
    const midpoint = (current.estimate + next.estimate) / 2;
    if (measurementValue <= midpoint) {
      return current.label;
    }
  }
  
  return sorted[sorted.length - 1].label;
};

// Helper: Calculate rate from typical job price
export const calculateRateFromTypical = (typicalPrice, typicalMeasurement) => {
  if (!typicalPrice || !typicalMeasurement || typicalMeasurement === 0) return null;
  return typicalPrice / typicalMeasurement;
};

// Helper: Format rate for display
export const formatRate = (rate, unit) => {
  if (rate == null) return '';
  const decimals = rate < 0.1 ? 4 : 2;
  return `$${rate.toFixed(decimals)}/${unit}`;
};

// Helper: Get all valid field service industry types
export const getFieldServiceIndustries = () => {
  return Object.keys(INDUSTRY_PRICING_TEMPLATES).filter(key => key !== 'Other');
};

// Helper: Check if industry is valid
export const isValidFieldServiceIndustry = (industryType) => {
  return industryType in INDUSTRY_PRICING_TEMPLATES;
};